import { useFormContext, get } from 'react-hook-form';
import { Box } from '@repo/ui/Box';
import { Flex } from '@repo/ui/Flex';
import { InputControl } from '@repo/ui/Input/InputControl';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { Button } from '@repo/ui/Button';
import { AddressForm } from '../../AddressForm/AddressForm';
import { getLocale } from '../../../utils/locale';
import { rawString, Text } from '../../Language/Language';
import type { TaskComponentType } from '../../../workflow/schemaTypes';
import s from './OrderlessTask.module.css';
import { InputMessage } from '@repo/ui/Input/InputMessage';

export const OrderlessTask: TaskComponentType = ({ handleNext, isLoading }) => {
  const { register, formState } = useFormContext();
  const { errors } = formState;
  const { country } = getLocale();

  return (
    <Box isContainer className={s.wrapper}>
      <form onSubmit={handleNext}>
        <Flex direction="column" gap="g-24">
          <h2 className={s.title}>
            <Text langKey="Global.orderless.title" />
          </h2>
          <p className={s.description}>
            <Text langKey="Global.orderless.description" />
          </p>

          <Flex direction="column" gap="g-16">
            <Flex direction="column" gap="g-12">
              <h3 className={s.sectionTitle}>
                <Text langKey="Global.orderless.contactInformation" />
              </h3>
              <Flex direction="row" gap="g-12" className={s.rowFields}>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.firstName')
                      ? 'error'
                      : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.firstName">
                        {rawString({
                          langKey: [
                            'Global.orderless.firstName',
                            'Global.customer.firstName',
                            'Global.firstName',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.firstName"
                        type="text"
                        {...register('orderlessCustomer.firstName')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.firstName') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.firstName')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.lastName')
                      ? 'error'
                      : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.lastName">
                        {rawString({
                          langKey: [
                            'Global.orderless.lastName',
                            'Global.customer.lastName',
                            'Global.lastName',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.lastName"
                        type="text"
                        {...register('orderlessCustomer.lastName')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.lastName') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.lastName')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
              </Flex>
              <InputRoot
                width="full"
                variant={
                  get(errors, 'orderlessCustomer.email') ? 'error' : 'default'
                }
              >
                <InputGroup>
                  <InputField>
                    <InputLabel htmlFor="orderlessCustomer.email">
                      {rawString({
                        langKey: [
                          'Global.orderless.email',
                          'Global.customer.email',
                          'Global.email',
                        ],
                      })}
                    </InputLabel>
                    <InputControl
                      id="orderlessCustomer.email"
                      type="email"
                      {...register('orderlessCustomer.email')}
                    />
                  </InputField>
                </InputGroup>
                {get(errors, 'orderlessCustomer.email') && (
                  <InputMessage>
                    {get(errors, 'orderlessCustomer.email')?.message}
                  </InputMessage>
                )}
              </InputRoot>
              <Flex direction="row" gap="g-12" className={s.rowFields}>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.phone') ? 'error' : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.phone">
                        {rawString({
                          langKey: [
                            'Global.orderless.phone',
                            'Global.customer.phone',
                            'Global.phone',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.phone"
                        type="tel"
                        {...register('orderlessCustomer.phone')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.phone') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.phone')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.phoneExtension')
                      ? 'error'
                      : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.phoneExtension">
                        {rawString({
                          langKey: [
                            'Global.orderless.phoneExtension',
                            'Global.customer.phoneExtension',
                            'Global.phoneExtension',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.phoneExtension"
                        {...register('orderlessCustomer.phoneExtension')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.phoneExtension') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.phoneExtension')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
              </Flex>
            </Flex>

            {/* Shipping Address */}
            <AddressForm
              name="orderlessCustomer.address"
              countryCode={country}
              baseLabel="Global.delivery.address"
            />
          </Flex>

          {/* Submit Button */}
          <Box margin="mt-32">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isLoading}
            >
              <Text langKey="Global.orderless.submitButton" />
            </Button>
          </Box>
        </Flex>
      </form>
    </Box>
  );
};
